"use client";

import { useEffect } from "react";
import { gtmScriptUrl } from "@/lib/site";

// Loads the GTM container, but not while the page is still painting.
//
// Why this exists instead of <GoogleTagManager> from @next/third-parties: that
// component injects gtm.js with next/script's `afterInteractive` strategy, which
// on mobile means gtm.js (170KB) and the gtag (187KB) and OneTrust (128KB) it
// pulls in behind it all compete with the hero image and the fonts for
// bandwidth. Measured on a Lighthouse mobile run of the home page: 485KB of the
// 1.18MB page, and blocking those two origins moved the score from 69 to 89 and
// LCP from 8.7s to 3.6s. Nothing else on the page comes close to that.
//
// The container still loads within a second or so of the page being usable, and
// window.dataLayer is created by lib/analytics.ts before anything pushes to it,
// so first-party events fired before the container arrives sit in the queue and
// are processed when it does.
//
// The one thing that genuinely waits is GTM's own auto-event listeners -- the
// All-Elements click trigger that fires clicked_on_sign_up lives inside gtm.js
// and cannot see a click that happens before it loads. That is what the
// interaction triggers below are for: the first pointer, key, touch or scroll
// starts the load, so by the time someone has moved from "touched the page" to
// "clicked the CTA" the container is there.

const GTM_SCRIPT_ID = "gtm-deferred";

// Backstop for the case where `load` never fires and the visitor never touches
// the page (a background tab, a bot, a stalled subresource). Long enough to be
// clear of LCP on a slow phone, short enough that the container is always up
// well within a normal session.
const MAX_DELAY_MS = 2500;

// Passive, capture-phase: we want to hear the very first interaction, including
// one on an element that stops propagation.
const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll"] as const;
const LISTENER_OPTIONS = { capture: true, passive: true } as const;

// Window.dataLayer is declared in lib/consent.ts, the other module that reaches
// for it directly.

function injectGtm(gtmId: string) {
  if (document.getElementById(GTM_SCRIPT_ID)) return;

  // The standard snippet's opening push. Kept adjacent to the injection rather
  // than done at mount so the gtm.start timestamp reports the container's real
  // load time instead of the page's.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const script = document.createElement("script");
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = gtmScriptUrl(gtmId);
  document.head.appendChild(script);
}

export function DeferredGoogleTagManager({ gtmId }: { gtmId: string }) {
  useEffect(() => {
    let done = false;
    let idleHandle: number | undefined;

    const start = () => {
      if (done) return;
      done = true;
      cleanup();
      injectGtm(gtmId);
    };

    // After `load`, wait for the first idle slice so the container never lands
    // in the middle of the browser finishing its own post-load work.
    const onLoad = () => {
      if (typeof window.requestIdleCallback === "function") {
        idleHandle = window.requestIdleCallback(start, { timeout: 1000 });
      } else {
        start();
      }
    };

    const timer = window.setTimeout(start, MAX_DELAY_MS);

    function cleanup() {
      window.clearTimeout(timer);
      window.removeEventListener("load", onLoad);
      for (const type of INTERACTION_EVENTS) {
        window.removeEventListener(type, start, LISTENER_OPTIONS);
      }
      if (idleHandle !== undefined && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      }
    }

    // A client-side navigation can mount this after `load` has already fired.
    if (document.readyState === "complete") {
      onLoad();
    } else {
      window.addEventListener("load", onLoad);
    }

    for (const type of INTERACTION_EVENTS) {
      window.addEventListener(type, start, LISTENER_OPTIONS);
    }

    return cleanup;
  }, [gtmId]);

  return null;
}
